<template>
  <div class="fb-version-history">
    <div
      v-if="loading"
      class="text-center py-4"
    >
      <b-spinner variant="primary" />
    </div>

    <div
      v-else-if="!versions.length"
      class="text-center text-muted py-4"
    >
      Nenhuma versão publicada ainda.
    </div>

    <div v-else>
      <div
        v-for="version in versions"
        :key="version.id"
        class="card border mb-2 p-2 fb-version-card"
      >
        <div class="d-flex align-items-start justify-content-between flex-wrap">
          <div>
            <strong>Versão {{ version.version_number }}</strong>
            <b-badge
              v-if="isCurrent(version)"
              variant="success"
              pill
              class="ml-1"
            >
              Versão atual
            </b-badge>
            <b-badge
              v-else
              :variant="version.source === 'publish' ? 'primary' : 'info'"
              pill
              class="ml-1"
            >
              {{ sourceBadgeText(version) }}
            </b-badge>

            <div class="text-muted small mt-1">
              <feather-icon
                icon="ClockIcon"
                size="11"
                class="mr-25"
              />
              {{ formatDate(version.created_at) }}
              <span v-if="version.created_by">
                · {{ version.created_by.name }}
              </span>
            </div>
          </div>

          <div class="fb-version-actions mt-1">
            <b-button
              size="sm"
              variant="outline-primary"
              class="mr-1"
              :disabled="previewingId === version.id"
              @click="previewVersion(version)"
            >
              <b-spinner
                v-if="previewingId === version.id"
                small
                class="mr-25"
              />
              <feather-icon
                v-else
                icon="EyeIcon"
                size="13"
                class="mr-25"
              />
              Pré-visualizar
            </b-button>
            <b-button
              v-if="!isCurrent(version)"
              size="sm"
              variant="outline-warning"
              :disabled="restoringId === version.id"
              @click="confirmRestore(version)"
            >
              <b-spinner
                v-if="restoringId === version.id"
                small
                class="mr-25"
              />
              <feather-icon
                v-else
                icon="RotateCwIcon"
                size="13"
                class="mr-25"
              />
              Restaurar
            </b-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { BButton, BBadge, BSpinner } from 'bootstrap-vue'
import swal from 'sweetalert'

export default {
  name: 'VersionHistory',
  components: { BButton, BBadge, BSpinner },
  props: {
    formDefinitionId: { type: [Number, String], required: true },
  },
  data() {
    return {
      versions: [],
      loading: false,
      previewingId: null,
      restoringId: null,
    }
  },
  mounted() {
    this.loadVersions()
  },
  methods: {
    // Exposto via $refs pelo componente pai (Index.vue) para recarregar o
    // histórico depois de uma publicação disparada fora deste componente
    // (badge de status ou botão "Publicar" na barra superior).
    loadVersions() {
      this.loading = true
      axios
        .get(`${this.$store.state.api}form_definitions/${this.formDefinitionId}/versions`)
        .then(r => {
          this.versions = r.data.data || []
        })
        .catch(e => this.$message(null, e.response?.data, 'error'))
        .finally(() => {
          this.loading = false
        })
    },

    // A versão vigente é sempre a primeira da lista (ordenada por
    // version_number DESC pelo backend) — não existe um campo "is_current".
    isCurrent(version) {
      return !!this.versions.length && this.versions[0].id === version.id
    },

    sourceBadgeText(version) {
      if (version.source === 'restore') {
        const origin = this.versions.find(v => v.id === version.restored_from_version_id)
        return `Restaurada da Versão ${origin ? origin.version_number : '?'}`
      }
      return 'Publicada'
    },

    formatDate(dateStr) {
      if (!dateStr) return '—'
      return window.moment(dateStr).format('DD/MM/YYYY HH:mm')
    },

    previewVersion(version) {
      this.previewingId = version.id
      axios
        .get(`${this.$store.state.api}form_definitions/${this.formDefinitionId}/versions/${version.id}`)
        .then(r => {
          this.$emit('preview', {
            fields: r.data.data.snapshot || [],
            versionNumber: version.version_number,
          })
        })
        .catch(e => this.$message(null, e.response?.data, 'error'))
        .finally(() => {
          this.previewingId = null
        })
    },

    confirmRestore(version) {
      swal({
        title: 'Restaurar esta versão?',
        text: `Isso substituirá a versão atual pelo formato da Versão ${version.version_number}. Nada é perdido — a versão atual continua no histórico.`,
        icon: 'warning',
        buttons: ['Cancelar', 'Restaurar'],
        dangerMode: true,
      }).then(confirmed => {
        if (!confirmed) return
        this.restoringId = version.id
        axios
          .post(`${this.$store.state.api}form_definitions/${this.formDefinitionId}/versions/${version.id}/restore`)
          .then(r => {
            this.$emit('restored', r.data.data)
          })
          .catch(e => this.$message(null, e.response?.data, 'error'))
          .finally(() => {
            this.restoringId = null
          })
      })
    },
  },
}
</script>

<style scoped>
.fb-version-card {
  background: #f9f9f9;
}
.dark-layout .fb-version-card {
  background: #283046;
  border-color: #3b4253 !important;
}
</style>
